import { useEffect } from 'react';
import { Delete } from 'lucide-react';

interface PinPadProps {
  pin: string;
  onChange: (pin: string) => void;
  onSubmit: (pin: string) => void;
  length?: number;
  disabled?: boolean;
}

// PIN-pad som matchar 05-pin-login.html-prototypen.
// - Visar fyra prickar som fylls i medan användaren skriver
// - Sista siffran triggar onSubmit() automatiskt
// - Stöd för både touch och keyboard (siffror + Backspace)
const KEYS: Array<{ value?: string; label: string; letters?: string; isAction?: boolean }> = [
  { value: '1', label: '1' },
  { value: '2', label: '2', letters: 'ABC' },
  { value: '3', label: '3', letters: 'DEF' },
  { value: '4', label: '4', letters: 'GHI' },
  { value: '5', label: '5', letters: 'JKL' },
  { value: '6', label: '6', letters: 'MNO' },
  { value: '7', label: '7', letters: 'PQRS' },
  { value: '8', label: '8', letters: 'TUV' },
  { value: '9', label: '9', letters: 'WXYZ' },
];

export function PinPad({
  pin,
  onChange,
  onSubmit,
  length = 4,
  disabled = false,
}: PinPadProps) {
  // Tangentbordsstöd. Vi lyssnar på fönstret för att kunna skriva direkt
  // utan att klicka på paden först.
  useEffect(() => {
    if (disabled) return;
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        appendDigit(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        backspace();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, disabled]);

  function appendDigit(d: string) {
    if (disabled) return;
    if (pin.length >= length) return;
    const next = pin + d;
    onChange(next);
    if (next.length === length) {
      onSubmit(next);
    }
  }

  function backspace() {
    if (disabled || pin.length === 0) return;
    onChange(pin.slice(0, -1));
  }

  return (
    <div
      className="grid grid-cols-3 gap-3.5 select-none"
      style={{ touchAction: 'manipulation' }}
    >
      {KEYS.map((k) => (
        <button
          key={k.label}
          type="button"
          disabled={disabled}
          onClick={() => k.value && appendDigit(k.value)}
          className={[
            'aspect-square rounded-2xl bg-white/[0.04] hover:bg-white/[0.08]',
            'border border-white/[0.06] hover:border-white/[0.12]',
            'text-loupe-text font-display font-normal',
            'flex flex-col items-center justify-center gap-0.5',
            'transition-all active:scale-[0.94] active:bg-white/[0.15]',
            'min-h-[64px] disabled:opacity-50 disabled:cursor-not-allowed',
            'text-3xl sm:text-3xl',
          ].join(' ')}
        >
          <span className="leading-none">{k.label}</span>
          {k.letters ? (
            <span className="text-[9px] font-sans font-semibold tracking-[0.2em] text-loupe-text-3 uppercase">
              {k.letters}
            </span>
          ) : null}
        </button>
      ))}

      {/* Action row: spacer | 0 | delete */}
      <div aria-hidden className="aspect-square" />
      <button
        type="button"
        disabled={disabled}
        onClick={() => appendDigit('0')}
        className={[
          'aspect-square rounded-2xl bg-white/[0.04] hover:bg-white/[0.08]',
          'border border-white/[0.06] hover:border-white/[0.12]',
          'text-loupe-text font-display font-normal text-3xl',
          'flex items-center justify-center',
          'transition-all active:scale-[0.94] active:bg-white/[0.15]',
          'min-h-[64px] disabled:opacity-50',
        ].join(' ')}
      >
        0
      </button>
      <button
        type="button"
        disabled={disabled || pin.length === 0}
        onClick={backspace}
        aria-label="Delete"
        className={[
          'aspect-square rounded-2xl bg-transparent hover:bg-white/[0.04]',
          'text-loupe-text-2 hover:text-white',
          'flex items-center justify-center',
          'transition-all active:scale-[0.94]',
          'min-h-[64px] disabled:opacity-30',
        ].join(' ')}
      >
        <Delete className="w-5 h-5" />
      </button>
    </div>
  );
}

interface PinDisplayProps {
  filled: number;
  total: number;
  state?: 'idle' | 'error' | 'success';
}

// Cirklarna ovanför paden. Showcasear "shake"-animation vid fel.
export function PinDisplay({ filled, total, state = 'idle' }: PinDisplayProps) {
  return (
    <div
      className={`flex justify-center items-center gap-4 h-7 ${
        state === 'error' ? 'animate-shake' : ''
      }`}
    >
      {Array.from({ length: total }).map((_, i) => {
        const isFilled = i < filled;
        let cls =
          'w-3.5 h-3.5 rounded-full border-[1.5px] transition-all duration-200';
        if (state === 'success') {
          cls += isFilled
            ? ' bg-emerald-400 border-emerald-400 scale-110'
            : ' border-loupe-border-2';
        } else if (state === 'error') {
          cls += ' bg-michelle border-michelle';
        } else if (isFilled) {
          cls +=
            ' bg-white border-white scale-110 shadow-[0_0_12px_rgba(255,255,255,0.4)]';
        } else {
          cls += ' bg-transparent border-loupe-border-2';
        }
        return <div key={i} className={cls} />;
      })}
    </div>
  );
}
